import { useState, useMemo } from 'react'
import { User, Filter, X, Clock, CheckCircle } from 'lucide-react'
import { useTestRuns } from '@/features/test-runs/api/useTestRuns'
import { useTestPlans } from '@/features/test-plans/api/useTestPlans'
import { useAuthStore } from '@/lib/auth/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { formatDate } from '@/lib/utils'



// ============================================================================
// Local type for test run rows (matches useTestRuns response shape)
// ============================================================================

import type { TestRun } from '@/types/testrun.types'

// ============================================================================
// Helpers
// ============================================================================

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '-'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>
    case 'running':
      return <Badge className="bg-blue-100 text-blue-800">Running</Badge>
    case 'pending':
      return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
    case 'cancelled':
      return <Badge variant="destructive">Cancelled</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

// ============================================================================
// Component
// ============================================================================

const MyExecutionsPage = () => {
  const { user } = useAuthStore()
  const [selectedTestPlanId, setSelectedTestPlanId] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const { data: testPlans } = useTestPlans()

  // Fetch test runs filtered by current user
  const filterParams = useMemo(() => {
    const params: {
      started_by?: number
      testplan_id?: number
      status?: string
      limit: number
    } = {
      limit: 100,
    }

    if (user?.id) {
      params.started_by = Number(user.id)
    }

    if (selectedTestPlanId !== 'all') {
      params.testplan_id = parseInt(selectedTestPlanId, 10)
    }

    if (selectedStatus !== 'all') {
      params.status = selectedStatus
    }

    return params
  }, [user?.id, selectedTestPlanId, selectedStatus])

  const { data: testRunsData, isLoading } = useTestRuns(filterParams)

  const testRuns = useMemo(() => {
    const runs: TestRun[] = testRunsData?.test_runs || []
    if (!searchQuery) return runs
    const query = searchQuery.toLowerCase()
    return runs.filter(
      (run: TestRun) =>
        run.name.toLowerCase().includes(query) ||
        run.testplan_name?.toLowerCase().includes(query) ||
        run.notes?.toLowerCase().includes(query)
    )
  }, [testRunsData, searchQuery])

  // Compute summary stats
  const stats = useMemo(() => {
    const all: TestRun[] = testRunsData?.test_runs || []
    return {
      total: all.length,
      completed: all.filter((r: TestRun) => r.status === 'completed').length,
      running: all.filter((r: TestRun) => r.status === 'running').length,
      pending: all.filter((r: TestRun) => r.status === 'pending').length,
      avgPassRate:
        all.length > 0
          ? all.reduce((acc: number, r: TestRun) => acc + (r.pass_rate ?? 0), 0) / all.length
          : 0,
    }
  }, [testRunsData])

  const activeFiltersCount = [
    selectedTestPlanId !== 'all',
    selectedStatus !== 'all',
    searchQuery.length > 0,
  ].filter(Boolean).length

  const handleClearFilters = () => {
    setSelectedTestPlanId('all')
    setSelectedStatus('all')
    setSearchQuery('')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">My Executions</h1>
        <p className="text-muted-foreground mt-1">
          Your personal test run history and execution results
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Runs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold">{stats.completed}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{stats.running}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Pass Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.avgPassRate >= 80 ? 'text-green-600' : stats.avgPassRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
              {stats.avgPassRate.toFixed(1)}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Test Run History</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {showFilters && (
          <CardContent className="border-b pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Search</label>
                <Input
                  placeholder="Search by name or test plan..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Test Plan</label>
                <Select value={selectedTestPlanId} onValueChange={setSelectedTestPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Test Plans" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Test Plans</SelectItem>
                    {(Array.isArray(testPlans) ? testPlans : []).map((plan) => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="running">Running</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        )}

        <CardContent className={showFilters ? 'pt-4' : ''}>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading your test runs...</div>
          ) : testRuns.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test Run</TableHead>
                    <TableHead>Test Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Pass Rate</TableHead>
                    <TableHead>Results</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testRuns.map((testRun: TestRun) => (
                    <TableRow key={testRun.test_run_id}>
                      <TableCell className="font-medium">{testRun.name}</TableCell>
                      <TableCell>{testRun.testplan_name || '-'}</TableCell>
                      <TableCell>{getStatusBadge(testRun.status)}</TableCell>
                      <TableCell>
                        <div className="w-32 space-y-1">
                          <div className="text-xs text-right text-muted-foreground">
                            {(testRun.progress_percentage ?? 0).toFixed(1)}%
                          </div>
                          <Progress value={testRun.progress_percentage ?? 0} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className={`font-medium ${
                            (testRun.pass_rate ?? 0) >= 80
                              ? 'text-green-600'
                              : (testRun.pass_rate ?? 0) >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {(testRun.pass_rate ?? 0).toFixed(1)}%
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">
                            {testRun.executed_tests}/{testRun.total_tests}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {testRun.passed_tests}&#10003; {testRun.failed_tests}&#10007;{' '}
                            {testRun.blocked_tests}&#8856;
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{testRun.started_at ? formatDate(testRun.started_at) : '-'}</TableCell>
                      <TableCell>{formatDuration(testRun.duration_seconds)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : activeFiltersCount > 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No test runs match your filters</p>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                You haven&apos;t started any test runs yet
              </p>
              <p className="text-sm text-muted-foreground">
                Test runs are created when you execute test plans
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default MyExecutionsPage
